import { useState } from "react";
import { CalendarDays, MapPin, MessageSquare, Camera, CheckCircle2, Ban } from "lucide-react";
import { useTranslation } from "react-i18next";
import { money } from "@/lib/format";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { TripPhotosDialog } from "@/components/TripPhotosDialog";

const STATUS = {
  confirmed: "bg-green-100 text-green-700",
  pending: "bg-amber-100 text-amber-700",
  completed: "bg-blue-100 text-blue-700",
  cancelled: "bg-slate-100 text-slate-500",
};
const today = new Date().toISOString().slice(0, 10);
const bucket = (t) => (t.status === "cancelled" ? "history" : t.return_date < today ? "history" : t.pickup_date <= today ? "active" : "upcoming");

export function HostTrips({ trips, onMessage, onRefresh }) {
  const { t: tr } = useTranslation();
  const [tab, setTab] = useState("upcoming");
  const [photoDlg, setPhotoDlg] = useState(null);
  const count = (b) => trips.filter((t) => bucket(t) === b).length;
  const filtered = trips.filter((t) => bucket(t) === tab);
  const cancelledCount = trips.filter((t) => t.status === "cancelled").length;

  const cancelTrip = async (t) => {
    if (!window.confirm(`Cancel this reservation for ${t.guest}? The dates will be freed.`)) return;
    try { await api.post(`/host/me/bookings/${t.id}/cancel`, { reason: "" }); toast.success("Reservation cancelled."); onRefresh?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-4" data-testid="host-trips">
      <div className="flex gap-2 flex-wrap items-center">
        {["upcoming", "active", "history"].map((b) => (
          <button key={b} data-testid={`trips-filter-${b}`} onClick={() => setTab(b)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium capitalize transition-colors ${tab === b ? "bg-blue-600 text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}>
            {tr(`hostTrips.${b}`)} ({count(b)})
          </button>
        ))}
        {cancelledCount > 0 && <span className="ml-auto text-xs font-semibold text-rose-500 bg-rose-50 px-3 py-1.5 rounded-full" data-testid="host-cancelled-count">{cancelledCount} cancelled</span>}
      </div>

      {filtered.length === 0 ? (
        <div data-testid="host-trips-empty" className="bg-white rounded-2xl shadow-card border border-slate-100 p-10 text-center text-slate-400">{tr("hostTrips.empty", { tab: tr(`hostTrips.${tab}`) })}</div>
      ) : (
        <div className="space-y-3" data-testid="host-trips-list">
          {filtered.map((t) => (
            <div key={t.id} data-testid={`host-trip-${t.id}`} className="bg-white rounded-2xl shadow-card border border-slate-100 p-5">
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <div className="flex items-center gap-3">
                    <h3 className="font-heading text-lg font-semibold text-slate-900">{t.car_name}</h3>
                    <span className={`text-xs font-semibold px-2.5 py-1 rounded-full capitalize ${STATUS[t.status] || "bg-slate-100 text-slate-500"}`}>{t.status}</span>
                  </div>
                  <p className="text-sm text-slate-600 mt-1.5">{tr("hostTrips.guest")} <span className="font-medium text-slate-900">{t.guest}</span></p>
                  <div className="flex flex-wrap gap-x-6 gap-y-1.5 mt-2 text-sm text-slate-500">
                    <span className="flex items-center gap-1.5"><CalendarDays size={14} /> {t.pickup_date} → {t.return_date} ({tr("common.daysShort", { count: t.days })})</span>
                    {t.pickup_location && <span className="flex items-center gap-1.5"><MapPin size={14} /> {t.pickup_location.split(",")[1]?.trim() || t.pickup_location}</span>}
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-xs text-slate-400">{tr("hostTrips.yourPayout")}</p>
                  <p className="font-heading text-xl font-bold text-slate-900">{money(t.host_payout)}</p>
                </div>
              </div>
              <div className="mt-4 pt-4 border-t border-slate-100 flex flex-wrap items-center gap-x-5 gap-y-2">
                <button onClick={() => onMessage(t)} data-testid={`host-trip-message-${t.id}`} className="flex items-center gap-2 text-sm font-semibold text-blue-600 hover:text-blue-700 transition-colors">
                  <MessageSquare size={15} /> {tr("hostTrips.message", { name: t.guest?.split(" ")[0] || tr("hostTrips.guestFallback") })}
                </button>
                {t.payment_status === "paid" && !t.photos_purged && (
                  <>
                    {(bucket(t) === "upcoming" || bucket(t) === "active") && (
                      t.trip_photos?.host_pre?.length ? (
                        <span className="text-sm text-emerald-600 inline-flex items-center gap-1.5"><CheckCircle2 size={15} />{tr("tripPhotos.preTripDone")}</span>
                      ) : (
                        <button onClick={() => setPhotoDlg({ id: t.id, stage: "host_pre", min: 4 })} data-testid={`host-pre-photos-${t.id}`} className="flex items-center gap-2 text-sm font-semibold text-blue-600 hover:text-blue-700 transition-colors"><Camera size={15} />{tr("tripPhotos.preTrip")}</button>
                      )
                    )}
                    {(bucket(t) === "active" || bucket(t) === "history") && (
                      t.trip_photos?.host_return?.length ? (
                        <span className="text-sm text-emerald-600 inline-flex items-center gap-1.5"><CheckCircle2 size={15} />{tr("tripPhotos.returnDone")}</span>
                      ) : (
                        <button onClick={() => setPhotoDlg({ id: t.id, stage: "host_return", min: 4 })} data-testid={`host-return-photos-${t.id}`} className="flex items-center gap-2 text-sm font-semibold text-blue-600 hover:text-blue-700 transition-colors"><Camera size={15} />{tr("tripPhotos.returnInspect")}</button>
                      )
                    )}
                  </>
                )}
                {t.photos_purged && <span className="text-xs text-slate-400">{tr("tripPhotos.purged")}</span>}
                {t.status !== "cancelled" && bucket(t) !== "history" && (
                  <button onClick={() => cancelTrip(t)} data-testid={`host-trip-cancel-${t.id}`} className="ml-auto flex items-center gap-1.5 text-sm font-semibold text-rose-500 hover:text-rose-600 transition-colors">
                    <Ban size={15} /> Cancel
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      {photoDlg && (
        <TripPhotosDialog
          open={!!photoDlg}
          onClose={() => setPhotoDlg(null)}
          endpoint={`/host/me/bookings/${photoDlg.id}/photos`}
          stage={photoDlg.stage}
          min={photoDlg.min}
          onDone={onRefresh}
        />
      )}
    </div>
  );
}
